import { Link } from '@remix-run/react'
import { motion } from 'framer-motion'
import AnimatedLink from './common/animated-link/animated-link'

export function Footer() {
	const currentYear = new Date().getFullYear()

	return (
		<motion.footer
			className="mt-auto w-full border-t-2 border-primary/30 bg-transparent backdrop-blur-sm"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="container mx-auto px-4 py-6 sm:py-8">
				<div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-3">
					{/* Brand Section */}
					<div className="space-y-3 sm:space-y-4">
						<motion.h3
							className="text-base font-semibold sm:text-lg"
							whileHover={{ scale: 1.05 }}
							transition={{ duration: 0.2 }}
						>
							Nicolás F. Mainella
						</motion.h3>
						<p className="max-w-xs text-xs text-foreground/70 sm:text-sm">
							Senior Software Engineer passionate about creating exceptional
							digital experiences with modern technologies.
						</p>
					</div>

					{/* Quick Links */}
					<div className="space-y-3 sm:space-y-4">
						<h4 className="text-xs font-medium uppercase tracking-wider text-foreground/60 sm:text-sm">
							Quick Links
						</h4>
						<nav className="flex flex-col space-y-1.5 sm:space-y-2">
							{[
								{ name: 'About', href: '/' },
								{ name: 'Work', href: '/work' },
								{ name: 'Contact', href: '/contact' },
							].map((link) => (
								<motion.div
									key={link.name}
									whileHover={{ x: 5 }}
									transition={{ duration: 0.2 }}
								>
									<Link
										to={link.href}
										className="text-xs text-foreground/70 transition-colors hover:text-foreground sm:text-sm"
									>
										{link.name}
									</Link>
								</motion.div>
							))}
						</nav>
					</div>

					{/* Connect Section */}
					<div className="space-y-3 sm:space-y-4">
						<h4 className="text-xs font-medium uppercase tracking-wider text-foreground/60 sm:text-sm">
							Connect
						</h4>
						<div className="flex space-x-3 sm:space-x-4">
							{[
								{
									name: 'LinkedIn',
									href: 'https://www.linkedin.com/in/nicolasfmainella/?locale=en_US',
									icon: (
										<svg
											className="h-5 w-5"
											fill="currentColor"
											viewBox="0 0 24 24"
										>
											<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
										</svg>
									),
								},
								{
									name: 'GitHub',
									href: 'https://github.com/nfmainella',
									icon: (
										<svg
											className="h-5 w-5"
											fill="currentColor"
											viewBox="0 0 24 24"
										>
											<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
										</svg>
									),
								},
							].map((social) => (
								<AnimatedLink
									key={social.name}
									to={social.href}
									rel="noopener noreferrer"
									className="text-foreground/70 transition-colors hover:text-foreground"
									whileHover={{ scale: 1.1, y: -2 }}
									whileTap={{ scale: 0.95 }}
									transition={{ duration: 0.2 }}
								>
									<div className="h-4 w-4 sm:h-5 sm:w-5">{social.icon}</div>
									<span className="sr-only">{social.name}</span>
								</AnimatedLink>
							))}
						</div>
					</div>
				</div>

				{/* Bottom Section */}
				<motion.div
					className="mt-6 flex flex-col items-center justify-between space-y-3 border-t border-border/20 pt-6 sm:mt-8 sm:space-y-4 sm:pt-8 md:flex-row md:space-y-0"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, delay: 0.2 }}
				>
					<p className="text-xs text-foreground/60 sm:text-sm">
						© {currentYear} Nicolás F. Mainella. All rights reserved.
					</p>
					<div className="flex items-end justify-start space-x-4 text-xs text-foreground/60 sm:text-sm">
						<span>
							Built with love using{' '}
							<AnimatedLink
								className="h-4 w-4"
								whileHover={{ scale: 1.1, y: -2 }}
								whileTap={{ scale: 0.95 }}
								transition={{ duration: 0.2 }}
								to="https://remix.run"
							>
								Remix
							</AnimatedLink>
						</span>
					</div>
				</motion.div>
			</div>
		</motion.footer>
	)
}
